import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    EventEmitter,
    inject,
    Injector,
    input,
    OnChanges,
    Output,
    runInInjectionContext,
    signal,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { PActionButtonModule } from '@panaya/ui/p-action-button';
import { PFieldTextComponent, PFieldTextModule, TextAreaEnum } from '@panaya/ui/p-field-text';
import { injectPromptContextComposer } from '@panaya/ai-chat/utils';
import { AiChatUiEventsService } from '../../../ai-chat-ui-events.service';
import { PromptBarService } from './prompt-bar.service';
import { PAttachmentPreviewComponent } from '@panaya/ui/p-attachment-preview';
import { PFileUploadButtonComponent } from '@panaya/ui/p-file-upload-button';
import { createUserPrompt } from '@panaya/ai-chat/shared';
import { AttachmentChipsService } from '../../../attachments/attachment-chips.service';
import { AttachmentSourcesMenuComponent } from '../../../attachments/attachment-sources-menu/attachment-sources-menu.component';
import { AttachmentSource, PickerAttachmentSource } from '../../../attachments/model/attachment-source.model';

@Component({
    standalone: true,
    selector: 'p-ai-chat-window-prompt-bar',
    imports: [
        PFieldTextModule,
        PActionButtonModule,
        FormsModule,
        TranslateModule,
        PAttachmentPreviewComponent,
        PFileUploadButtonComponent,
        AttachmentSourcesMenuComponent
    ],
    templateUrl: './ai-chat-window-prompt-bar.component.html',
    styleUrl: './ai-chat-window-prompt-bar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiChatWindowPromptBarComponent implements OnChanges {
    private readonly events = inject(AiChatUiEventsService);
    private readonly promptBarService = inject(PromptBarService);
    private readonly chips = inject(AttachmentChipsService);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly injector = inject(Injector);
    private readonly destroyRef = inject(DestroyRef);
    private readonly composeContext = injectPromptContextComposer();

    protected readonly input = signal('');
    protected readonly displayWarning = signal(false);
    protected readonly fileToUpload = signal<File | undefined>(undefined);
    protected readonly inputSize = TextAreaEnum.FullWidth;
    protected readonly showFileUpload = toSignal(this.promptBarService.showFileUpload$, { initialValue: false });
    protected readonly allowedSources = this.chips.allowedSources;
    protected readonly attachmentChips = this.chips.chips;
    /** The "+" menu shows on a `REQUEST_RESOURCES` turn that names at least one registered item. */
    protected readonly showSourcesMenu = computed(() => this.allowedSources().length > 0);
    protected readonly fileAccept = computed(() => this.chips.resourceRequestOptions()?.accept?.join(',') ?? '');
    protected readonly fileMultiple = computed(() => this.chips.resourceRequestOptions()?.multiple ?? false);
    protected readonly showStopButton = computed(() => !this.disabled() && this.disabledSubmit());
    protected readonly canSubmit = computed(
        () =>
            !this.disabledSubmit() &&
            !this.disabled() &&
            (this.input().length > 0 || this.fileToUpload() !== undefined || this.chips.hasSendableChips())
    );

    @ViewChild(PFieldTextComponent) private readonly promptBox?: PFieldTextComponent;
    @Output() public onTriggerPrompt = new EventEmitter<void>();

    public readonly disabled = input(false);
    public readonly disabledSubmit = input(false);

    constructor() {
        this.events.suggestionRequest.pipe(takeUntilDestroyed()).subscribe((suggestion: string) => this.changeInput(suggestion));
    }

    public ngOnChanges(changes: SimpleChanges): void {
        // When the prompt is sent and the text area gets disabled,
        // focus leaves the text area. This restores the focus after
        // the prompt has been processed and the component is enabled again.
        if (changes['disabled'] && !this.disabled()) {
            this.promptBox?.focus();
        }
    }

    protected triggerInterrupt(): void {
        this.events.interruptRequest.emit();
    }

    protected onInputChanged(value: string): void {
        this.input.set(value);

        this.displayWarning.set(false);
    }

    protected async onEnterPressed(event: KeyboardEvent): Promise<void> {
        if (event.shiftKey) {
            return;
        }

        if (this.canSubmit()) {
            await this.triggerPrompt();
        }
    }

    protected async triggerPrompt(): Promise<void> {
        const file = this.fileToUpload();

        if (file) {
            this.handleFileUpload(file, await file.text());
        } else {
            this.handleInput();
        }

        this.clearState();

        this.onTriggerPrompt.emit();
    }

    /** A file source opens the browser's file dialog; a picker source opens the host's picker. */
    protected onSourceSelected(source: AttachmentSource, fileInput: HTMLInputElement): void {
        if (source.kind === 'file') {
            fileInput.click();

            return;
        }

        this.pick(source);
    }

    protected onFilesChosen(fileInput: HTMLInputElement): void {
        this.chips.addFiles(Array.from(fileInput.files ?? []));

        // Lets the user choose the same file again.
        fileInput.value = '';
    }

    protected removeChip(id: number): void {
        this.chips.remove(id);
    }

    protected onFileSelected(file: File): void {
        this.fileToUpload.set(file);
    }

    protected removeFile(): void {
        this.fileToUpload.set(undefined);
    }

    private pick(source: PickerAttachmentSource): void {
        runInInjectionContext(this.injector, () => source.pick())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((attachments) => this.chips.addPicked(source, attachments));
    }

    private handleFileUpload(file: File, fileContent: string): void {
        this.events.promptRequest.emit(createUserPrompt({ message: fileContent, context: this.composeContext(), file }));
    }

    private handleInput(): void {
        const attachments = this.chips.takeSendableAttachments();

        if (this.input().trim() === '' && attachments.length === 0) {
            this.displayWarning.set(true);

            return;
        }

        this.events.promptRequest.emit(
            createUserPrompt({
                message: this.input(),
                context: this.composeContext(),
                ...(attachments.length > 0 ? { contextExtras: { attachments }, attachments } : {})
            })
        );
    }

    private changeInput(value: string): void {
        this.input.set(value);

        // Run change detection so the textarea will resize if the last
        // contents made it larger
        this.changeDetector.detectChanges();
    }

    private clearState(): void {
        this.changeInput('');

        this.fileToUpload.set(undefined);
    }
}
